import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Percent, Calendar, TrendingUp, Info } from 'lucide-react';
import { brand, theme } from './theme';

// Tooltip Component with mobile-friendly positioning
const Tooltip = ({ children, content }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState('left');

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    
    // Simple positioning: if icon is in right half of screen, position tooltip to the left
    // Otherwise position to the right
    if (rect.left > viewportWidth / 2) {
      setPosition('left');
    } else {
      setPosition('right');
    }
    
    setIsVisible(true);
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div 
          className={`absolute z-50 p-2 sm:p-3 text-xs text-white bg-gray-800 rounded-lg shadow-lg bottom-full mb-2 w-48 sm:w-64
            ${position === 'left' ? 'right-0' : 'left-0'}
          `}
        >
          <div className="relative">
            {content}
            <div 
              className={`absolute top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800
                ${position === 'left' ? 'right-2' : 'left-2'}
              `}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const MortgageCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(300000);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [downPayment, setDownPayment] = useState(60000);
  const [propertyTax, setPropertyTax] = useState(3600);
  const [homeInsurance, setHomeInsurance] = useState(1200);
  const [hoaFees, setHoaFees] = useState(0);
  const [loanType, setLoanType] = useState('conventional');
  const [showAmortization, setShowAmortization] = useState(false);
  const [pmiMode, setPmiMode] = useState('estimate');
  const [manualPMI, setManualPMI] = useState(0);
  const [pmiManualMode, setPmiManualMode] = useState('estimate');
  const [creditScore, setCreditScore] = useState('');
  const [downPaymentMode, setDownPaymentMode] = useState('dollar');
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [propertyTaxMode, setPropertyTaxMode] = useState('estimate');
  const [homeInsuranceMode, setHomeInsuranceMode] = useState('estimate');
  const [hoaFeesMode, setHoaFeesMode] = useState('annual');
  const [results, setResults] = useState({});
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);

  const loanTypes = {
    conventional: {
      name: 'Conventional',
      minDownPayment: 0.03,
      pmiRequired: true,
      pmiThreshold: 0.20,
      fundingFee: 0,
      description: 'Standard loan with flexible terms'
    },
    fha: {
      name: 'FHA',
      minDownPayment: 0.035,
      pmiRequired: true,
      pmiThreshold: 1,
      upfrontMIP: 0.0175,
      annualMIP: 0.0055,
      fundingFee: 0,
      description: 'Government-backed with low down payment'
    },
    va: {
      name: 'VA',
      minDownPayment: 0,
      pmiRequired: false,
      fundingFee: 0.023,
      description: 'For eligible veterans and service members'
    },
    usda: {
      name: 'USDA',
      minDownPayment: 0,
      pmiRequired: true,
      pmiThreshold: 1,
      upfrontFee: 0.01,
      annualFee: 0.0035,
      fundingFee: 0,
      description: 'For eligible rural and suburban areas'
    }
  };

  const formatNumberInput = (value) => {
    if (!value) return '';
    return Number(value).toLocaleString('en-US');
  };

  const parseFormattedNumber = (value) => {
    return Number(value.replace(/,/g, ''));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyDetailed = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const setPropertyTaxModeDirectly = (newMode) => {
    // Handle value conversion
    if (propertyTax > 0 && propertyTaxMode !== newMode) {
      if (newMode === 'monthly') {
        // Converting TO monthly (from either annual or estimate)
        if (propertyTaxMode === 'annual' || propertyTaxMode === 'estimate') {
          const convertedValue = Math.round((propertyTax / 12) * 100) / 100;
          setPropertyTax(convertedValue);
        }
      } else if (newMode === 'annual') {
        // Converting TO annual (from monthly)
        if (propertyTaxMode === 'monthly') {
          const convertedValue = Math.round((propertyTax * 12) * 100) / 100;
          setPropertyTax(convertedValue);
        }
      }
    }
    
    setPropertyTaxMode(newMode);
    
    // Generate estimate if switching to estimate mode
    if (newMode === 'estimate') {
      const estimatedAnnualTax = loanAmount * 0.012;
      setPropertyTax(Math.round(estimatedAnnualTax));
    }
  };

  const setHomeInsuranceModeDirectly = (newMode) => {
    // Handle value conversion
    if (homeInsurance > 0 && homeInsuranceMode !== newMode) {
      if (newMode === 'monthly') {
        // Converting TO monthly (from either annual or estimate)
        if (homeInsuranceMode === 'annual' || homeInsuranceMode === 'estimate') {
          const convertedValue = Math.round((homeInsurance / 12) * 100) / 100;
          setHomeInsurance(convertedValue);
        }
      } else if (newMode === 'annual') {
        // Converting TO annual (from monthly)
        if (homeInsuranceMode === 'monthly') {
          const convertedValue = Math.round((homeInsurance * 12) * 100) / 100;
          setHomeInsurance(convertedValue);
        }
      }
    }
    
    setHomeInsuranceMode(newMode);
    
    // Generate estimate if switching to estimate mode
    if (newMode === 'estimate') {
      const estimatedAnnualInsurance = loanAmount * 0.004;
      setHomeInsurance(Math.round(estimatedAnnualInsurance));
    }
  };

  const setPmiModeDirectly = (newMode) => {
    const currentMode = pmiMode;
    
    // Handle value conversion between annual and monthly
    if (manualPMI > 0 && currentMode !== 'estimate' && newMode !== 'estimate') {
      if (newMode === 'monthly' && currentMode === 'annual') {
        setManualPMI(Math.round((manualPMI / 12) * 100) / 100);
      } else if (newMode === 'annual' && currentMode === 'monthly') {
        setManualPMI(Math.round((manualPMI * 12) * 100) / 100);
      }
    }
    
    setPmiMode(newMode);
    setPmiManualMode(newMode);
    
    // Reset manual PMI if switching to estimate mode
    if (newMode === 'estimate') {
      setManualPMI(0);
    }
  };

  const setHoaFeesModeDirectly = (newMode) => {
    // Handle value conversion between annual and monthly
    if (hoaFees > 0 && hoaFeesMode !== newMode) {
      if (newMode === 'monthly' && hoaFeesMode === 'annual') {
        const convertedValue = Math.round((hoaFees / 12) * 100) / 100;
        setHoaFees(convertedValue);
      } else if (newMode === 'annual' && hoaFeesMode === 'monthly') {
        const convertedValue = Math.round((hoaFees * 12) * 100) / 100;
        setHoaFees(convertedValue);
      }
    }
    
    setHoaFeesMode(newMode);
  };

  const handleDownPaymentChange = (value, mode) => {
    const numericValue = parseFormattedNumber(value);
    if (mode === 'dollar') {
      setDownPayment(numericValue);
      setDownPaymentPercent((numericValue / loanAmount * 100).toFixed(1));
    } else {
      const dollarAmount = (numericValue / 100) * loanAmount;
      setDownPayment(dollarAmount);
      setDownPaymentPercent(numericValue);
    }
  };

  const handleLoanAmountChange = (amount) => {
    const numericAmount = parseFormattedNumber(amount);
    setLoanAmount(numericAmount);
    if (downPaymentMode === 'percent') {
      const newDownPayment = (downPaymentPercent / 100) * numericAmount;
      setDownPayment(newDownPayment);
    }
  };

  const handleLoanTypeChange = (newLoanType) => {
    setLoanType(newLoanType);
    const newConfig = loanTypes[newLoanType];
    const minDownPaymentAmount = loanAmount * newConfig.minDownPayment;
    
    if (downPayment < minDownPaymentAmount) {
      setDownPayment(Math.ceil(minDownPaymentAmount));
      setDownPaymentPercent((Math.ceil(minDownPaymentAmount) / loanAmount * 100).toFixed(1));
    }
  };

  const calculatePMIRate = (loanToValue, creditScore) => {
    const pmiRates = {
      excellent: { low: 0.003, medium: 0.004, high: 0.005 },
      good: { low: 0.005, medium: 0.007, high: 0.010 },
      fair: { low: 0.008, medium: 0.011, high: 0.015 },
      poor: { low: 0.012, medium: 0.015, high: 0.020 }
    };

    let scoreCategory;
    if (creditScore >= 740) scoreCategory = 'excellent';
    else if (creditScore >= 680) scoreCategory = 'good';
    else if (creditScore >= 620) scoreCategory = 'fair';
    else scoreCategory = 'poor';

    let ltvCategory;
    if (loanToValue <= 0.80) ltvCategory = 'low';
    else if (loanToValue <= 0.90) ltvCategory = 'medium';
    else ltvCategory = 'high';

    return pmiRates[scoreCategory][ltvCategory];
  };

  const calculateMortgage = () => {
    const principal = loanAmount - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;
    const loanConfig = loanTypes[loanType];
    const downPaymentPercent = downPayment / loanAmount;
    
    let monthlyMI = 0;
    let upfrontFee = 0;
    let fundingFee = 0;

    if (loanType === 'conventional') {
      if (downPaymentPercent < 0.20) {
        if (pmiMode === 'estimate') {
          if (creditScore && creditScore !== '') {
            const score = parseInt(creditScore);
            const loanToValue = principal / loanAmount;
            const pmiRate = calculatePMIRate(loanToValue, score);
            monthlyMI = (principal * pmiRate) / 12;
          } else {
            monthlyMI = (principal * 0.005) / 12;
          }
        } else {
          monthlyMI = pmiMode === 'monthly' ? Number(manualPMI) : Number(manualPMI) / 12;
        }
      }
    } else if (loanType === 'fha') {
      upfrontFee = loanAmount * 0.0175;
      if (pmiMode === 'estimate') {
        monthlyMI = (loanAmount * 0.0055) / 12;
      } else {
        monthlyMI = pmiMode === 'monthly' ? Number(manualPMI) : Number(manualPMI) / 12;
      }
    } else if (loanType === 'va') {
      fundingFee = loanAmount * 0.023;
    } else if (loanType === 'usda') {
      upfrontFee = loanAmount * 0.01;
      if (pmiMode === 'estimate') {
        monthlyMI = (loanAmount * 0.0035) / 12;
      } else {
        monthlyMI = pmiMode === 'monthly' ? Number(manualPMI) : Number(manualPMI) / 12;
      }
    }
    
    const monthlyPI = principal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                      (Math.pow(1 + monthlyRate, numPayments) - 1);
    
    const monthlyPropertyTax = propertyTaxMode === 'monthly' ? propertyTax : propertyTax / 12;
    const monthlyInsurance = homeInsuranceMode === 'monthly' ? homeInsurance : homeInsurance / 12;
    const monthlyHOA = hoaFeesMode === 'annual' ? hoaFees / 12 : hoaFees;
    
    const totalMonthlyPayment = monthlyPI + monthlyPropertyTax + monthlyInsurance + monthlyHOA + monthlyMI;
    
    const totalInterest = (monthlyPI * numPayments) - principal;
    const totalOfPayments = monthlyPI * numPayments;
    const minDownPaymentAmount = loanAmount * loanConfig.minDownPayment;
    
    setResults({
      monthlyPI: monthlyPI,
      monthlyPropertyTax: monthlyPropertyTax,
      monthlyInsurance: monthlyInsurance,
      monthlyHOA: monthlyHOA,
      monthlyMI: monthlyMI,
      totalMonthlyPayment: totalMonthlyPayment,
      principal: principal,
      totalInterest: totalInterest,
      totalOfPayments: totalOfPayments,
      downPaymentPercent: downPaymentPercent,
      minDownPaymentAmount: minDownPaymentAmount,
      upfrontFee: upfrontFee,
      fundingFee: fundingFee,
      loanConfig: loanConfig
    });
    
    let balance = principal;
    const schedule = [];
    
    for (let month = 1; month <= Math.min(12, numPayments); month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPI - interestPayment;
      balance -= principalPayment;
      
      schedule.push({
        month,
        payment: monthlyPI,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance)
      });
    }
    
    setAmortizationSchedule(schedule);
  };

  useEffect(() => {
    // Update estimates when home price changes
    if (propertyTaxMode === 'estimate') {
      const estimatedAnnualTax = loanAmount * 0.012;
      setPropertyTax(Math.round(estimatedAnnualTax));
    }
    if (homeInsuranceMode === 'estimate') {
      const estimatedAnnualInsurance = loanAmount * 0.004;
      setHomeInsurance(Math.round(estimatedAnnualInsurance));
    }
    
    calculateMortgage();
  }, [loanAmount, interestRate, loanTerm, downPayment, propertyTax, homeInsurance, hoaFees, loanType, pmiMode, manualPMI, pmiManualMode, creditScore, downPaymentMode, downPaymentPercent, propertyTaxMode, homeInsuranceMode, hoaFeesMode]);

  return (
    <div className={`max-w-6xl mx-auto p-2 sm:p-6 ${theme.pageBg} min-h-screen`}>
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8 mb-4 sm:mb-8">
        {/* Brand header -- wordmark and accent both come from theme.js */}
        <div className={`${theme.headerBar} text-white p-4 sm:p-6 rounded-t-xl`}>
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Calculator className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              {brand.name && (
                <div className="text-white/90 font-bold text-lg sm:text-xl tracking-wide">{brand.name}</div>
              )}
              <h1 className="text-base sm:text-lg font-bold text-white/95">Mortgage Calculator</h1>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          <div className="space-y-4 sm:space-y-6">
            <div className="mt-4">
              <div className="flex items-center gap-1 mb-2">
                <label className="block text-sm font-semibold text-gray-800">Loan Type</label>
                <Tooltip content="Different loan types have different requirements and benefits. Conventional loans are most common, FHA has lower down payment requirements, VA loans are for veterans, and USDA loans are for eligible rural areas.">
                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Tooltip>
              </div>
              <select
                value={loanType}
                onChange={(e) => handleLoanTypeChange(e.target.value)}
                className={`w-full px-2 py-2 text-sm border border-gray-300 rounded ${theme.inputFocus1}`}
              >
                {Object.entries(loanTypes).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center gap-1 mb-2">
                <label className="block text-sm font-semibold text-gray-800">Home Price</label>
                <Tooltip content="The total purchase price of the home you're buying. This is the amount you've agreed to pay the seller.">
                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                </Tooltip>
              </div>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  value={formatNumberInput(loanAmount)}
                  onChange={(e) => handleLoanAmountChange(e.target.value)}
                  className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2}`}
                  placeholder="300,000"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1">
                  <label className="block text-sm font-semibold text-gray-800">Down Payment</label>
                  <Tooltip content="The amount you'll pay upfront when purchasing the home. A larger down payment reduces your loan amount and may help you avoid PMI.">
                    <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </Tooltip>
                </div>
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => setDownPaymentMode('dollar')}
                    className={`px-2 sm:px-3 py-1 text-xs rounded transition-colors ${
                      downPaymentMode === 'dollar'
                        ? theme.toggleActive
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    $
                  </button>
                  <button
                    type="button"
                    onClick={() => setDownPaymentMode('percent')}
                    className={`px-2 sm:px-3 py-1 text-xs rounded transition-colors ${
                      downPaymentMode === 'percent'
                        ? theme.toggleActive
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    %
                  </button>
                </div>
              </div>
              <div className="relative">
                {downPaymentMode === 'dollar' ? (
                  <>
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formatNumberInput(downPayment)}
                      onChange={(e) => handleDownPaymentChange(e.target.value, 'dollar')}
                      className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2}`}
                      placeholder="60,000"
                    />
                  </>
                ) : (
                  <>
                    <Percent className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
                    <input
                      type="number"
                      step="0.1"
                      value={downPaymentPercent}
                      onChange={(e) => handleDownPaymentChange(e.target.value, 'percent')}
                      className={`w-full pl-3 sm:pl-4 pr-9 sm:pr-10 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2}`}
                      placeholder="20"
                      min="0"
                      max="100"
                    />
                  </>
                )}
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                {downPaymentMode === 'dollar' 
                  ? `${((downPayment / loanAmount) * 100).toFixed(1)}% of home price`
                  : `${formatCurrency(downPayment)} of home price`
                }
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <label className="block text-sm font-semibold text-gray-800">Interest Rate</label>
                  <Tooltip content="The annual interest rate for your mortgage. This affects your monthly payment amount.">
                    <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </Tooltip>
                </div>
                <div className="relative">
                  <Percent className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className={`w-full pl-3 sm:pl-4 pr-9 sm:pr-10 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2}`}
                    placeholder="6.5"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-1 mb-2">
                  <label className="block text-sm font-semibold text-gray-800">Loan Term</label>
                  <Tooltip content="The number of years you'll take to repay the loan. Shorter terms have higher monthly payments but lower total interest.">
                    <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </Tooltip>
                </div>
                <div className="relative">
                  <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
                  <select
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(Number(e.target.value))}
                    className={`w-full pl-3 sm:pl-4 pr-9 sm:pr-10 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2} appearance-none`}
                  >
                    <option value={15}>15 years</option>
                    <option value={20}>20 years</option>
                    <option value={25}>25 years</option>
                    <option value={30}>30 years</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 sm:pt-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Additional Costs</h3>
              
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <label className="block text-sm font-semibold text-gray-800">Property Tax</label>
                      <Tooltip content="Annual property taxes you'll pay to your local government. This is typically 1-2% of your home's value annually.">
                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => setPropertyTaxModeDirectly('estimate')}
                        className={`px-1.5 sm:px-2 py-1 text-xs rounded transition-colors ${
                          propertyTaxMode === 'estimate'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Est</span>
                        <span className="hidden sm:inline">Estimate</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPropertyTaxModeDirectly('annual')}
                        className={`px-1.5 sm:px-2 py-1 text-xs rounded transition-colors ${
                          propertyTaxMode === 'annual'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Ann</span>
                        <span className="hidden sm:inline">Annual</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPropertyTaxModeDirectly('monthly')}
                        className={`px-1.5 sm:px-2 py-1 text-xs rounded transition-colors ${
                          propertyTaxMode === 'monthly'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Mon</span>
                        <span className="hidden sm:inline">Monthly</span>
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formatNumberInput(propertyTax)}
                      onChange={(e) => setPropertyTax(parseFormattedNumber(e.target.value))}
                      className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2}`}
                      placeholder={propertyTaxMode === 'annual' ? '3,600' : '300'}
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {propertyTaxMode === 'estimate' 
                      ? `Estimated at 1.2% of home value annually (${formatCurrency(propertyTax / 12)}/month)`
                      : propertyTaxMode === 'annual' 
                        ? `Monthly equivalent: ${formatCurrency(propertyTax / 12)}`
                        : `Annual equivalent: ${formatCurrency(propertyTax * 12)}`
                    }
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <label className="block text-sm font-semibold text-gray-800">Home Insurance</label>
                      <Tooltip content="Homeowner's insurance protects your property from damage and liability. Required by most lenders.">
                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => setHomeInsuranceModeDirectly('estimate')}
                        className={`px-1.5 sm:px-2 py-1 text-xs rounded transition-colors ${
                          homeInsuranceMode === 'estimate'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Est</span>
                        <span className="hidden sm:inline">Estimate</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setHomeInsuranceModeDirectly('annual')}
                        className={`px-1.5 sm:px-2 py-1 text-xs rounded transition-colors ${
                          homeInsuranceMode === 'annual'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Ann</span>
                        <span className="hidden sm:inline">Annual</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setHomeInsuranceModeDirectly('monthly')}
                        className={`px-1.5 sm:px-2 py-1 text-xs rounded transition-colors ${
                          homeInsuranceMode === 'monthly'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Mon</span>
                        <span className="hidden sm:inline">Monthly</span>
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formatNumberInput(homeInsurance)}
                      onChange={(e) => setHomeInsurance(parseFormattedNumber(e.target.value))}
                      className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2}`}
                      placeholder={homeInsuranceMode === 'annual' ? '1,200' : '100'}
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {homeInsuranceMode === 'estimate' 
                      ? `Estimated at 0.4% of home value annually (${formatCurrency(homeInsurance / 12)}/month)`
                      : homeInsuranceMode === 'annual' 
                        ? `Monthly equivalent: ${formatCurrency(homeInsurance / 12)}`
                        : `Annual equivalent: ${formatCurrency(homeInsurance * 12)}`
                    }
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <label className="block text-sm font-semibold text-gray-800">HOA Fees</label>
                      <Tooltip content="Homeowners Association fees for community maintenance and amenities. Only applies if your property has an HOA.">
                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => setHoaFeesModeDirectly('annual')}
                        className={`px-1.5 sm:px-3 py-1 text-xs rounded transition-colors ${
                          hoaFeesMode === 'annual'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Ann</span>
                        <span className="hidden sm:inline">Annual</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setHoaFeesModeDirectly('monthly')}
                        className={`px-1.5 sm:px-3 py-1 text-xs rounded transition-colors ${
                          hoaFeesMode === 'monthly'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Mon</span>
                        <span className="hidden sm:inline">Monthly</span>
                      </button>
                    </div>
                  </div>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formatNumberInput(hoaFees)}
                      onChange={(e) => setHoaFees(parseFormattedNumber(e.target.value))}
                      className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2}`}
                      placeholder="0"
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {hoaFees > 0 ? (
                      hoaFeesMode === 'annual' 
                        ? `Monthly equivalent: ${formatCurrency(hoaFees / 12)}`
                        : `Annual equivalent: ${formatCurrency(hoaFees * 12)}`
                    ) : (
                      'Enter 0 if no HOA or fees included elsewhere'
                    )}
                  </p>
                </div>
              </div>

              {(loanType !== 'va' && ((loanType === 'conventional' && (downPayment / loanAmount) < 0.20) || loanType === 'fha' || loanType === 'usda')) && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <label className="block text-sm font-semibold text-gray-800">
                        {loanType === 'fha' ? 'MIP' : loanType === 'usda' ? 'Guarantee Fee' : 'PMI'}
                      </label>
                      <Tooltip content={
                        loanType === 'fha' 
                          ? "FHA Mortgage Insurance Premium is required on all FHA loans. Includes upfront (1.75%) and annual (0.55%) premiums."
                          : loanType === 'usda'
                          ? "USDA Guarantee Fee is required on all USDA loans. Includes upfront (1%) and annual (0.35%) fees."
                          : "Private Mortgage Insurance is required when down payment is less than 20%. Can be removed once you reach 20% equity."
                      }>
                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </Tooltip>
                    </div>
                    <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
                      <button
                        type="button"
                        onClick={() => setPmiModeDirectly('estimate')}
                        className={`px-1.5 sm:px-2 py-1 text-xs rounded transition-colors ${
                          pmiMode === 'estimate'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Est</span>
                        <span className="hidden sm:inline">Estimate</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPmiModeDirectly('annual')}
                        className={`px-1.5 sm:px-2 py-1 text-xs rounded transition-colors ${
                          pmiMode === 'annual'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Ann</span>
                        <span className="hidden sm:inline">Annual</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setPmiModeDirectly('monthly')}
                        className={`px-1.5 sm:px-2 py-1 text-xs rounded transition-colors ${
                          pmiMode === 'monthly'
                            ? theme.toggleActive
                            : 'text-gray-600 hover:text-gray-800'
                        }`}
                      >
                        <span className="sm:hidden">Mon</span>
                        <span className="hidden sm:inline">Monthly</span>
                      </button>
                    </div>
                  </div>

                  {pmiMode === 'estimate' ? (
                    <div>
                      {loanType === 'conventional' && (
                        <div className="mb-3">
                          <div className="flex items-center gap-1 mb-2">
                            <label className="block text-sm font-semibold text-gray-800">
                              Credit Score (Optional)
                            </label>
                            <Tooltip 
                              content={
                                <div>
                                  <strong>How Credit Score Affects PMI</strong>
                                  <br />• <strong>740+:</strong> Excellent - Lowest PMI rates (0.3% - 0.5%)
                                  <br />• <strong>680-739:</strong> Good - Moderate PMI rates (0.5% - 1.0%)
                                  <br />• <strong>620-679:</strong> Fair - Higher PMI rates (0.8% - 1.5%)
                                  <br />• <strong>Below 620:</strong> Poor - Highest PMI rates (1.2% - 2.0%)
                                  <br /><br />Higher credit scores result in significantly lower monthly PMI costs.
                                </div>
                              }
                            >
                              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </Tooltip>
                          </div>
                          <input
                            type="number"
                            value={creditScore}
                            onChange={(e) => setCreditScore(e.target.value)}
                            placeholder="e.g. 720"
                            min="300"
                            max="850"
                            className={`w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2}`}
                          />
                        </div>
                      )}
                      <div className="text-xs sm:text-sm text-gray-600">
                        {loanType === 'conventional' && (
                          <>
                            {creditScore ? (
                              <>Using credit score {creditScore} for PMI calculation</>
                            ) : (
                              <>Using standard PMI rate (0.5% annually)</>
                            )}
                          </>
                        )}
                        {loanType === 'fha' && <>Using FHA MIP rates (1.75% upfront + 0.55% annually)</>}
                        {loanType === 'usda' && <>Using USDA guarantee fee rates (1% upfront + 0.35% annually)</>}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
                        <input
                          type="text"
                          value={formatNumberInput(manualPMI)}
                          onChange={(e) => setManualPMI(parseFormattedNumber(e.target.value))}
                          className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg ${theme.inputFocus2}`}
                          placeholder={pmiMode === 'annual' ? 'Enter annual amount' : 'Enter monthly amount'}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {manualPMI > 0 ? (
                          pmiMode === 'annual' 
                            ? `Monthly equivalent: ${formatCurrency(manualPMI / 12)}`
                            : `Annual equivalent: ${formatCurrency(manualPMI * 12)}`
                        ) : (
                          `Enter ${pmiMode} ${loanType === 'fha' ? 'MIP' : loanType === 'usda' ? 'guarantee fee' : 'PMI'} amount`
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-4 sm:space-y-6">
            <div className={`${theme.panelStrong} rounded-xl p-4 sm:p-6`}>
              <h3 className={`text-lg sm:text-xl font-bold ${theme.headingAccent} mb-3 sm:mb-4`}>
                Monthly Payment Breakdown ({loanTypes[loanType].name})
              </h3>
              
              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm sm:text-base text-gray-700">Principal & Interest:</span>
                  <span className="font-semibold text-sm sm:text-base text-gray-900">{formatCurrency(results.monthlyPI || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm sm:text-base text-gray-700">Property Tax:</span>
                  <span className="font-semibold text-sm sm:text-base text-gray-900">{formatCurrency(results.monthlyPropertyTax || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm sm:text-base text-gray-700">Home Insurance:</span>
                  <span className="font-semibold text-sm sm:text-base text-gray-900">{formatCurrency(results.monthlyInsurance || 0)}</span>
                </div>
                {results.monthlyHOA > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm sm:text-base text-gray-700">HOA Fees:</span>
                    <span className="font-semibold text-sm sm:text-base text-gray-900">{formatCurrency(results.monthlyHOA || 0)}</span>
                  </div>
                )}
                {(loanType !== 'va' && ((results.monthlyMI > 0) || (loanType === 'conventional' && (downPayment / loanAmount) < 0.20) || loanType === 'fha' || loanType === 'usda')) && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-sm sm:text-base text-gray-700">
                        {loanType === 'fha' ? 'MIP:' : loanType === 'usda' ? 'Guarantee Fee:' : 'PMI:'}
                      </span>
                      <Tooltip 
                        content={
                          <div>
                            {loanType === 'fha' && (
                              <div>
                                <strong>MIP (Mortgage Insurance Premium)</strong>
                                <br />• Required on all FHA loans
                                <br />• Upfront: 1.75% of loan amount
                                <br />• Annual: 0.55% paid monthly
                                <br />• <strong>Removal:</strong> Can be removed after 11 years if LTV ≤ 78%
                              </div>
                            )}
                            {loanType === 'conventional' && (
                              <div>
                                <strong>PMI (Private Mortgage Insurance)</strong>
                                <br />• Required when down payment &lt; 20%
                                <br />• Rate varies by credit score (0.3% - 1.5%)
                                <br />• <strong>Removal:</strong> Automatically removed at 78% LTV
                              </div>
                            )}
                            {loanType === 'usda' && (
                              <div>
                                <strong>USDA Guarantee Fee</strong>
                                <br />• Required on all USDA loans
                                <br />• Upfront: 1% of loan amount
                                <br />• Annual: 0.35% paid monthly
                                <br />• <strong>Removal:</strong> Required for life of loan
                              </div>
                            )}
                          </div>
                        }
                      >
                        <Info className={`w-4 h-4 ${theme.iconAccent}`} />
                      </Tooltip>
                    </div>
                    <span className="font-semibold text-sm sm:text-base text-gray-900">
                      {formatCurrency(results.monthlyMI || 0)}
                    </span>
                  </div>
                )}
                <div className={`border-t ${theme.borderAccent} pt-2 sm:pt-3 mt-2 sm:mt-3`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-base sm:text-lg font-bold ${theme.headingAccent}`}>Total Monthly Payment:</span>
                    <span className={`text-xl sm:text-2xl font-bold ${theme.headingAccent}`}>{formatCurrency(results.totalMonthlyPayment || 0)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className={`${theme.panelSoft} rounded-xl p-4 sm:p-6`}>
              <h3 className={`text-lg sm:text-xl font-bold ${theme.headingAccent} mb-3 sm:mb-4`}>Loan Summary</h3>
              
              <div className="space-y-2 sm:space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm sm:text-base text-gray-700">Loan Amount:</span>
                  <span className="font-semibold text-sm sm:text-base text-gray-900">{formatCurrency(results.principal || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm sm:text-base text-gray-700">Down Payment:</span>
                  <span className="font-semibold text-sm sm:text-base text-gray-900">
                    {formatCurrency(downPayment)} ({(results.downPaymentPercent * 100 || 0).toFixed(1)}%)
                  </span>
                </div>
				
				{/*client didn't want to see this chunk but I think it's good, so keeping as comment*/}
				{/*
                {results.upfrontFee > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm sm:text-base text-gray-700">
                      {loanType === 'fha' ? 'Upfront MIP:' : 'Upfront Fee:'}
                    </span>
                    <span className="font-semibold text-sm sm:text-base text-gray-900">{formatCurrency(results.upfrontFee)}</span>
                  </div>
                )}
                {results.fundingFee > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-1">
                      <span className="text-sm sm:text-base text-gray-700">VA Funding Fee:</span>
                      <Tooltip 
                        content={
                          <div>
                            <strong>VA Funding Fee</strong>
                            <br />• One-time fee for VA loan program
                            <br />• Current rate: 2.3% for first-time use with 0% down
                            <br />• Can be rolled into the loan amount
                            <br />• <strong>Exemptions:</strong> Veterans with disabilities, Purple Heart recipients
                          </div>
                        }
                      >
                        <Info className={`w-4 h-4 ${theme.iconAccent}`} />
                      </Tooltip>
                    </div>
                    <span className="font-semibold text-sm sm:text-base text-gray-900">{formatCurrency(results.fundingFee)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm sm:text-base text-gray-700">Total Interest:</span>
                  <span className="font-semibold text-sm sm:text-base text-gray-900">{formatCurrency(results.totalInterest || 0)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm sm:text-base text-gray-700">Total of Payments:</span>
                  <span className="font-semibold text-sm sm:text-base text-gray-900">{formatCurrency(results.totalOfPayments || 0)}</span>
                </div>
				*/}
				
              </div>
            </div>

            <button
              onClick={() => setShowAmortization(!showAmortization)}
              className={`w-full ${theme.primaryButton} font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm sm:text-base`}
            >
              <TrendingUp className="w-4 sm:w-5 h-4 sm:h-5" />
              {showAmortization ? 'Hide' : 'Show'} Amortization Schedule
            </button>
          </div>
        </div>

        {/* Amortization Schedule */}
        {showAmortization && (
          <div className="mt-6 sm:mt-8 border-t pt-6 sm:pt-8">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Amortization Schedule (First 12 Months)</h3>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">Month</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-medium text-gray-700">Payment</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-medium text-gray-700">Principal</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-medium text-gray-700">Interest</th>
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-right text-xs sm:text-sm font-medium text-gray-700">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {amortizationSchedule.map((row) => (
                    <tr key={row.month} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900">{row.month}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 text-right">{formatCurrencyDetailed(row.payment)}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 text-right">{formatCurrencyDetailed(row.principal)}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 text-right">{formatCurrencyDetailed(row.interest)}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-900 text-right">{formatCurrency(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MortgageCalculator;